/**
 * 发声通路选择。
 *
 * 华为 / 荣耀的 ArkWeb 内核浏览器会给 Web Audio 的渲染流单独上一个低增益，
 * 实测同一份满刻度采样走 <audio> 明显比走 AudioContext 响（用户机型 A/B 确认）。
 * 电平问题在数字侧无解，只能换通路：节拍音交给媒体元素播放，拍点排期仍用
 * AudioContext 的音频时钟，因此时序精度不受影响。
 */

export type SoundOutputMode = 'webaudio' | 'media'

const STORAGE_KEY = 'metronome.outputMode'

/** 是否手机 / 平板浏览器：部分规避只对移动端有意义，不必让桌面版承担副作用 */
export function isMobileBrowser(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  // iPadOS 13+ 的 Safari 伪装成 Macintosh，靠触控点数量区分
  return (
    /android|iphone|ipad|ipod|harmony|openharmony/i.test(ua) ||
    (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1)
  )
}

/**
 * 是否需要默认走媒体通路。
 * 只针对有实测证据的 ArkWeb / 华为系内核；其他安卓浏览器（Chrome 等）的 Web Audio 电平正常，
 * 没必要一上来就换通路，用户仍可手动打开开关。
 */
export function prefersMediaOutput(): boolean {
  const ua = navigator.userAgent
  return isMobileBrowser() && /arkweb|huawei|openharmony/i.test(ua)
}

/** 用户是否手动选过；没选过返回 null，交给 prefersMediaOutput() 决定 */
export function readStoredOutputMode(): SoundOutputMode | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    return value === 'webaudio' || value === 'media' ? value : null
  } catch {
    // 隐私模式下 localStorage 会抛异常，忽略即可
    return null
  }
}

export function storeOutputMode(mode: SoundOutputMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode)
  } catch {
    // 无法持久化就只在本次会话生效
  }
}

/** 解析初始通路：用户选择优先，其次按内核特征自动判定 */
export function resolveOutputMode(): SoundOutputMode {
  return readStoredOutputMode() ?? (prefersMediaOutput() ? 'media' : 'webaudio')
}
